import { useEffect, useMemo, useState } from 'react';
import { customROITimePeriods } from '../constants';
import { useHasRoiFeature } from '../hooks/use-has-roi-feature';
import { ARRoiOverviewCard } from './ar-roi-overview-card';
import { BTRoiOverviewCard } from './bt-roi-overview-card';
import { Carousel } from './carousel';
import { MCTRoiOverviewCard } from './mct-roi-overview-card';
import { OverviewCardProps, RoiCards } from './types';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  setHasRoiCards?: (value?: boolean) => void;
}

export const RoiOverviewCards = ({ setHasRoiCards, ...rest }: Props) => {
  const [activeCards, setActiveCards] = useState<RoiCards>({});
  const [onScreenCardsCount, setOnScreenCardsCount] = useState<number>(0);

  const { isFeatureEnabled: isAppointmentRemindersRoiEnabled } = useHasRoiFeature({ roi: 'appointment-reminders' });
  const { isFeatureEnabled: isBulkTextingRoiEnabled } = useHasRoiFeature({ roi: 'bulk-texting' });
  const { isFeatureEnabled: isMissedCallTextRoiEnabled } = useHasRoiFeature({ roi: 'missedCallText' });

  const activeCardsCount = useMemo(() => {
    return Object.values(activeCards).filter(Boolean).length;
  }, [activeCards]);

  const commonProps: OverviewCardProps = {
    activeCardsCount,
    hasOnlyOneCard: activeCardsCount === 1 && onScreenCardsCount === 1,
    timePeriod: customROITimePeriods.lastQuarter,
    handleUpdateActiveCards: (card: keyof RoiCards, value?: boolean) => {
      setActiveCards((prev) => ({
        ...prev,
        [card]: value,
      }));
    },
    onScreenCardsCount,
  };

  useEffect(() => {
    setHasRoiCards?.(!!activeCardsCount);
  }, [activeCardsCount, setHasRoiCards]);

  return (
    <Carousel {...rest} onScreenCardsCountChangeHandler={setOnScreenCardsCount}>
      <Carousel.Item isVisible={activeCards.missedCallText}>
        {isMissedCallTextRoiEnabled && <MCTRoiOverviewCard {...commonProps} />}
      </Carousel.Item>
      <Carousel.Item isVisible={activeCards.bulkText}>
        {isBulkTextingRoiEnabled && <BTRoiOverviewCard {...commonProps} />}
      </Carousel.Item>
      <Carousel.Item isVisible={activeCards.appointmentReminders}>
        {isAppointmentRemindersRoiEnabled && <ARRoiOverviewCard {...commonProps} />}
      </Carousel.Item>
    </Carousel>
  );
};
